import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  TechniquesApiResponse,
  WavelightMeasurements,
  WavelightPerson,
  WavelightStudy,
  WavelightsState,
  WavesApiResponse,
} from './types';
import {
  Pagination,
  Technique,
  Wave,
  WavelightAlgorithms,
  WaveTechniques,
} from '../../types';
import {
  disableItemRequest,
  downloadVideoRequest,
  getAllResearchForItem,
  getAllTechniquesForItem,
  getAllWavelightItemsRequest,
  getAllWavesForItem,
  getTechniqueRequest,
  getWavelightItemRequest,
  getWavelightWaveRequest,
  saveMeasurementsRequest,
  savePersonRequest,
  saveStudyRequest,
  startAlgorithmCalculationRequest,
  startTechniqueCalculationRequest,
} from './userAPI';
import storage from '../../utils/storage';

const ITEMS_PER_PAGE_KEY = '@wavelight/itemsPerPage';

// Get All Wavelights
export const getAllWavelights = createAsyncThunk(
  'wavelight/get-all-wavelight-items',
  async ({ limit, page }: Pagination) => {
    return await getAllWavelightItemsRequest({ limit, page });
  },
);

// Get Wavelight Item
export const getWavelightItem = createAsyncThunk(
  'wavelight/get-wavelight-item',
  async (id: string) => {
    return await getWavelightItemRequest(id);
  },
);

// Download Video
export const downloadVideo = createAsyncThunk(
  'wavelight/download-video',
  async (id: string) => {
    const { url } = await downloadVideoRequest(id);
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
);

// Disable Item
export const disableItem = createAsyncThunk(
  'wavelight/disable-wavelight',
  async (id: string) => {
    await disableItemRequest(id);
    return id;
  },
);

export const savePerson = createAsyncThunk(
  'wavelight/save-person',
  async ({ id, person }: { id: string; person: WavelightPerson }) => {
    return await savePersonRequest(id, person);
  },
);

export const saveStudy = createAsyncThunk(
  'wavelight/save-study',
  async ({ id, study }: { id: string; study: WavelightStudy }) => {
    return await saveStudyRequest(id, study);
  },
);

export const saveMeasurements = createAsyncThunk(
  'wavelight/save-measurements',
  async ({
    id,
    measurements,
  }: {
    id: string;
    measurements: WavelightMeasurements;
  }) => {
    return await saveMeasurementsRequest(id, measurements);
  },
);

export const getAllWavelightWaves = createAsyncThunk(
  'wavelight-waves/get-waves',
  async ({ id, limit, page }: { id: string } & Pagination) => {
    return await getAllWavesForItem({ id, limit, page });
  },
);

export const getAllWavelightResearch = createAsyncThunk(
  'wavelight-waves/get-research',
  async ({ id, limit, page }: { id: string } & Pagination) => {
    const response = await getAllResearchForItem({ id, limit, page });
    return response;
  },
);

export const startAlgorithmCalculation = createAsyncThunk(
  'wavelight/start-algorithm-calculation',
  async ({ id, algorithm }: { id: string; algorithm: WavelightAlgorithms }) => {
    return await startAlgorithmCalculationRequest(id, algorithm);
  },
);

export const getWavelightWave = createAsyncThunk(
  'wavelight-waves/get-wave',
  async ({ id, wid }: { id: string; wid: string }) => {
    return await getWavelightWaveRequest(id, wid);
  },
);

export const startTechniqueCalculation = createAsyncThunk(
  'wavelight-wave-technique/start-technique-calculation',
  async ({ id, technique }: { id: string; technique: WaveTechniques }) => {
    return await startTechniqueCalculationRequest(id, technique);
  },
);

export const getAllWaveTechniques = createAsyncThunk(
  'wavelight-wave-technique/get-techniques-results',
  async ({ id, limit, page }: { id: string } & Pagination) => {
    return await getAllTechniquesForItem({ id, page, limit });
  },
);

export const getTechnique = createAsyncThunk(
  'wavelight-wave-technique/get-technique',
  async ({ id }: { id: string }) => {
    return await getTechniqueRequest(id);
  },
);

const initialState: WavelightsState = {
  // Control
  isLoading: false,
  error: null,
  // Wavelight Items
  wavelightsResponse: null,
  wavelight: null,
  // Wavelight Waves
  waveLightAlgorithms: [
    WavelightAlgorithms.UNDEFINED,
    WavelightAlgorithms.AVERAGE_OF_COLORS,
  ],
  selectedAlgorithm: WavelightAlgorithms.UNDEFINED,
  algorithmCalculationLoading: false,
  algorithmCalculationResponse: null,
  wavesResponse: null,
  wave: null,
  // Wavelight Wave Techniques
  waveTechniques: [WaveTechniques.UNDEFINED, WaveTechniques.HEART_BEATS],
  selectedTechnique: WaveTechniques.UNDEFINED,
  techniqueCalculationLoading: false,
  techniqueCalculationResponse: null,
  techniquesResponse: null,
  technique: null,
  itemsPerPage: storage.get(ITEMS_PER_PAGE_KEY) || 10,

  research: null,
};

export const captureSlice = createSlice({
  name: 'wavelight',
  initialState,
  reducers: {
    // Control
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Wavelight Items
    setPerson: (
      state: WavelightsState,
      { payload }: PayloadAction<WavelightPerson>,
    ) => {
      if (state.wavelight) {
        state.wavelight.person = payload;
      }
    },
    setStudy: (
      state: WavelightsState,
      { payload }: PayloadAction<WavelightStudy>,
    ) => {
      if (state.wavelight) {
        state.wavelight.study = payload;
      }
    },
    setMeasurements: (
      state: WavelightsState,
      { payload }: PayloadAction<WavelightMeasurements>,
    ) => {
      if (state.wavelight) {
        state.wavelight.measurements = payload;
      }
    },
    // Wavelight Waves
    setAlgorithm: (
      state: WavelightsState,
      { payload }: PayloadAction<WavelightAlgorithms>,
    ) => {
      state.selectedAlgorithm = payload;
    },
    // Wavelight Wave Techniques
    setTechnique: (
      state: WavelightsState,
      { payload }: PayloadAction<WaveTechniques>,
    ) => {
      state.selectedTechnique = payload;
    },
    saveItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      storage.set(ITEMS_PER_PAGE_KEY, action.payload);
    },
  },
  extraReducers: (builder) => {
    // Get all Wavelights
    builder.addCase(getAllWavelights.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.wavelightsResponse = payload;
    });
    builder.addCase(getAllWavelights.rejected, (state, { error }) => {
      state.isLoading = false;
      state.error = error.message as string;
    });

    // Get Wavelight Item
    builder.addCase(getWavelightItem.fulfilled, (state, { payload }) => {
      state.wavelight = payload;
    });

    // Deactivate Wavelight Item
    builder.addCase(disableItem.fulfilled, (state, { payload }) => {
      if (state.wavelightsResponse)
        state.wavelightsResponse.wavelights =
          state.wavelightsResponse.wavelights.filter((item) => {
            return item._id !== payload;
          });
    });

    // Wavelight Items
    builder.addCase(savePerson.fulfilled, (state, { payload }) => {
      if (state.wavelight) {
        state.wavelight.person = payload;
      }
    });

    builder.addCase(saveStudy.fulfilled, (state, { payload }) => {
      if (state.wavelight) {
        state.wavelight.study = payload;
      }
    });

    builder.addCase(saveMeasurements.fulfilled, (state, { payload }) => {
      if (state.wavelight) {
        state.wavelight.measurements = payload;
      }
    });

    // Case to get all waves for a wavelight id
    builder.addCase(
      getAllWavelightWaves.fulfilled,
      (
        state: WavelightsState,
        { payload }: PayloadAction<WavesApiResponse>,
      ) => {
        state.wavesResponse = payload;
      },
    );

    // Case to get a single wave for a wavelight id
    builder.addCase(
      getWavelightWave.fulfilled,
      (state: WavelightsState, { payload }: PayloadAction<Wave>) => {
        state.wave = payload;
      },
    );

    builder.addCase(
      startAlgorithmCalculation.pending,
      (state: WavelightsState) => {
        state.algorithmCalculationLoading = true;
      },
    );

    builder.addCase(
      startAlgorithmCalculation.fulfilled,
      (state: WavelightsState, { payload }) => {
        state.algorithmCalculationLoading = false;
        state.algorithmCalculationResponse = payload;
      },
    );

    builder.addCase(
      startAlgorithmCalculation.rejected,
      (state: WavelightsState, { payload }) => {
        state.algorithmCalculationLoading = false;
        state.algorithmCalculationResponse = payload;
      },
    );

    builder.addCase(
      startTechniqueCalculation.pending,
      (state: WavelightsState) => {
        state.techniqueCalculationLoading = true;
      },
    );

    builder.addCase(
      startTechniqueCalculation.fulfilled,
      (state: WavelightsState, { payload }) => {
        state.techniqueCalculationLoading = false;
        state.techniqueCalculationResponse = payload;
      },
    );

    builder.addCase(
      startTechniqueCalculation.rejected,
      (state: WavelightsState, { payload }) => {
        state.techniqueCalculationLoading = false;
        state.techniqueCalculationResponse = payload;
      },
    );

    // Case to get all technique results for a wave id
    builder.addCase(
      getAllWaveTechniques.fulfilled,
      (
        state: WavelightsState,
        { payload }: PayloadAction<TechniquesApiResponse>,
      ) => {
        state.techniquesResponse = payload;
      },
    );

    builder.addCase(
      getTechnique.fulfilled,
      (state: WavelightsState, { payload }: PayloadAction<Technique>) => {
        state.technique = payload;
      },
    );

    // Case to get all research for a wavelight id
    builder.addCase(
      getAllWavelightResearch.fulfilled,
      (state: WavelightsState, { payload }) => {
        state.research = payload;
      },
    );

    builder.addCase(
      getAllWavelightResearch.rejected,
      (state: WavelightsState, { error }) => {
        state.error = error.message as string;
      },
    );
  },
});

export const {
  // Control
  setLoading,
  // Wavelight Items
  setPerson,
  setStudy,
  setMeasurements,
  // Wavelight Waves
  setAlgorithm,
  // Wavelight Wave Techniques
  setTechnique,
  saveItemsPerPage,
} = captureSlice.actions;

export default captureSlice.reducer;
